import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useTranslation } from '../../store/languageStore';
import SectionHeader from './SectionHeader';
import { searchItems, MasterDataItem } from '../../services/masterDataService';

interface Item {
  item_number: number;
  description: string;
  hs_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_value: number;
  origin_country: string;
  net_weight: number;
  gross_weight: number;
}

interface ItemsTabProps {
  data: Item[];
  onChange: (data: Item[]) => void;
}

const ItemsTab: React.FC<ItemsTabProps> = ({ data, onChange }) => {
  const { t, tString } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Master-data product lookup (pull a confirmed catalog product into this line)
  const [productOptions, setProductOptions] = useState<MasterDataItem[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const searchTimer = useRef<any>(null);

  const handleProductSearch = (value: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!value || value.trim().length < 2) {
      setProductOptions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setProductSearching(true);
      try {
        setProductOptions(await searchItems(value.trim()));
      } catch {
        setProductOptions([]);
      } finally {
        setProductSearching(false);
      }
    }, 300);
  };

  const handleSelectProduct = (product: MasterDataItem | null) => {
    if (!product || !editingItem) return;
    // Fill the customs-relevant fields from the reviewed catalog record;
    // keep existing values where the catalog has none.
    setEditingItem({
      ...editingItem,
      description: product.enriched_description || product.raw_description || editingItem.description,
      hs_code: product.hs_code || editingItem.hs_code,
      origin_country: product.origin_country || editingItem.origin_country,
    });
  };

  const emptyItem: Item = {
    item_number: (data?.length || 0) + 1,
    description: '',
    hs_code: '',
    quantity: 0,
    unit: 'PCE',
    unit_price: 0,
    total_value: 0,
    origin_country: '',
    net_weight: 0,
    gross_weight: 0
  };

  const handleAddItem = () => {
    setEditingItem({ ...emptyItem });
    setEditingIndex(null);
    setOpenDialog(true);
  };

  const handleEditItem = (index: number) => {
    setEditingItem({ ...data[index] });
    setEditingIndex(index);
    setOpenDialog(true);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = data.filter((_, i) => i !== index);
    // Renumber items
    const renumbered = newItems.map((item, i) => ({
      ...item,
      item_number: i + 1
    }));
    onChange(renumbered);
  };

  const handleSaveItem = () => {
    if (!editingItem) return;

    const newItems = [...(data || [])];
    if (editingIndex !== null) {
      newItems[editingIndex] = editingItem;
    } else {
      newItems.push(editingItem);
    }
    onChange(newItems);
    setOpenDialog(false);
    setEditingItem(null);
    setEditingIndex(null);
  };

  const handleItemChange = (field: keyof Item, value: any) => {
    if (!editingItem) return;

    const updated = { ...editingItem, [field]: value };

    // Auto-calculate total value
    if (field === 'quantity' || field === 'unit_price') {
      updated.total_value = Number(updated.quantity) * Number(updated.unit_price);
    }

    setEditingItem(updated);
  };

  const calculateTotals = () => {
    if (!data || data.length === 0) {
      return { totalValue: 0, totalWeight: 0, itemCount: 0 };
    }

    return {
      totalValue: data.reduce((sum, item) => sum + Number(item.total_value || 0), 0),
      totalWeight: data.reduce((sum, item) => sum + Number(item.gross_weight || 0), 0),
      itemCount: data.length
    };
  };

  const totals = calculateTotals();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <SectionHeader
          title={t('customsDeclaration.items.title') as string}
          subtitle={t('customsDeclaration.items.subtitle') as string}
          icon={<InventoryIcon color="primary" sx={{ fontSize: 32 }} />}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          sx={{ mt: 1 }}
        >
          {t('customsDeclaration.items.addItem')}
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Items Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>#</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>HS Code</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">Total Value</TableCell>
              <TableCell>Origin</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.item_number}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.hs_code}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell align="right">{item.unit_price.toFixed(2)}</TableCell>
                  <TableCell align="right">{item.total_value.toFixed(2)}</TableCell>
                  <TableCell>{item.origin_country}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEditItem(index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteItem(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No items added yet. Click "Add Item" to begin.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {/* Totals Row */}
            {data && data.length > 0 && (
              <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 'bold' }}>
                <TableCell colSpan={3}>
                  <Typography variant="subtitle2">
                    Total ({totals.itemCount} items)
                  </Typography>
                </TableCell>
                <TableCell colSpan={3} />
                <TableCell align="right">
                  <Typography variant="subtitle2">
                    {totals.totalValue.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    Weight: {totals.totalWeight.toFixed(2)} kg
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Item Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingIndex !== null ? 'Edit Item' : 'Add New Item'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={productOptions}
                loading={productSearching}
                filterOptions={(x) => x}
                getOptionLabel={(o: MasterDataItem) =>
                  o.enriched_description || o.raw_description || ''
                }
                onInputChange={(_e, value) => handleProductSearch(value)}
                onChange={(_e, value) => handleSelectProduct(value as MasterDataItem | null)}
                renderOption={(props, o: MasterDataItem) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography variant="body2">
                        {o.enriched_description || o.raw_description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[o.sku, o.hs_code && `HS ${o.hs_code}`, o.origin_country]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search product catalog (Master Data)"
                    placeholder="Type a product name, SKU, or HS code to auto-fill"
                    helperText="Optional — pulls HS code, description & origin from your reviewed catalog"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {productSearching ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Item Description"
                value={editingItem?.description || ''}
                onChange={(e) => handleItemChange('description', e.target.value)}
                required
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="HS Code"
                value={editingItem?.hs_code || ''}
                onChange={(e) => handleItemChange('hs_code', e.target.value)}
                helperText="Harmonized System Code (6-10 digits)"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Origin Country"
                value={editingItem?.origin_country || ''}
                onChange={(e) => handleItemChange('origin_country', e.target.value)}
                placeholder="e.g., DE, CN, US"
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={editingItem?.quantity || ''}
                onChange={(e) => handleItemChange('quantity', Number(e.target.value))}
                inputProps={{ min: 0, step: 1 }}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Unit"
                value={editingItem?.unit || 'PCE'}
                onChange={(e) => handleItemChange('unit', e.target.value)}
                helperText="e.g., PCE, KG, M"
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={editingItem?.unit_price || ''}
                onChange={(e) => handleItemChange('unit_price', Number(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Total Value"
                type="number"
                value={editingItem?.total_value || 0}
                InputProps={{ readOnly: true }}
                helperText="Auto-calculated"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Net Weight (kg)"
                type="number"
                value={editingItem?.net_weight || ''}
                onChange={(e) => handleItemChange('net_weight', Number(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Gross Weight (kg)"
                type="number"
                value={editingItem?.gross_weight || ''}
                onChange={(e) => handleItemChange('gross_weight', Number(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveItem} variant="contained">
            Save Item
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemsTab;
